import apiClient from '../apiClient';

/**
 * Transform backend ProductDTO to frontend product model
 */
export const transformBackendToFrontend = (backendProduct) => {
    return {
        id: backendProduct.publicId,
        itemCode: backendProduct.itemCode,
        name: backendProduct.name,
        price: backendProduct.salesPriceWholesale || 0,
        retailPrice: backendProduct.salesPriceRetail || 0,
        discountedPrice: backendProduct.discountedPriceWholesale,
        discountedRetailPrice: backendProduct.discountedPriceRetail,
        stock: backendProduct.availableQuantity || 0,
        unit: backendProduct.unit || '',
        active: backendProduct.active !== false,
        category: backendProduct.category || '',
        vatRate: backendProduct.vatRate !== undefined ? backendProduct.vatRate : 5,
        priceIncludesVat: backendProduct.priceIncludesVat !== undefined ? backendProduct.priceIncludesVat : false,
        effectivePrice: backendProduct.effectivePrice,
        effectiveDiscountedPrice: backendProduct.effectiveDiscountedPrice,
        image: backendProduct.imageUrl || '',
        thumbnailUrl: backendProduct.thumbnailUrl || '',
        iconClass: 'fa-solid fa-box',
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600'
    };
};

/**
 * Transform frontend product model to backend CreateProductRequest
 */
export const transformFrontendToCreateRequest = (frontendProduct) => {
    return {
        itemCode: frontendProduct.itemCode,
        name: frontendProduct.name,
        salesPriceRetail: frontendProduct.retailPrice || 0,
        discountedPriceRetail: frontendProduct.discountedRetailPrice || null,
        salesPriceWholesale: frontendProduct.price || 0,
        discountedPriceWholesale: frontendProduct.discountedPrice || null,
        availableQuantity: frontendProduct.stock || 0,
        unit: frontendProduct.unit || '',
        category: frontendProduct.category || null,
        active: frontendProduct.active !== false,
        vatRate: frontendProduct.vatRate !== undefined ? frontendProduct.vatRate : 5,
        priceIncludesVat: frontendProduct.priceIncludesVat !== undefined ? frontendProduct.priceIncludesVat : false,
        imageUrl: frontendProduct.image || null,
        thumbnailUrl: frontendProduct.thumbnailUrl || null
    };
};

/**
 * Transform frontend product model to backend UpdateProductRequest
 */
export const transformFrontendToUpdateRequest = (frontendProduct) => {
    return {
        name: frontendProduct.name,
        salesPriceRetail: frontendProduct.retailPrice || 0,
        discountedPriceRetail: frontendProduct.discountedRetailPrice || null,
        salesPriceWholesale: frontendProduct.price || 0,
        discountedPriceWholesale: frontendProduct.discountedPrice || null,
        availableQuantity: frontendProduct.stock || 0,
        unit: frontendProduct.unit || '',
        category: frontendProduct.category || null,
        active: frontendProduct.active !== false,
        vatRate: frontendProduct.vatRate !== undefined ? frontendProduct.vatRate : 5,
        priceIncludesVat: frontendProduct.priceIncludesVat !== undefined ? frontendProduct.priceIncludesVat : false,
        imageUrl: frontendProduct.image || null,
        thumbnailUrl: frontendProduct.thumbnailUrl || null
    };
};

/**
 * Get all products for the current tenant
 */
export const getAllProducts = async (params = {}) => {
    try {
        const { search = '', category = '', page = 0, size = 10, status = 'ACTIVE', sort = 'createdAt,desc' } = params;
        
        // Translate frontend sort fields to backend database fields
        let backendSort = sort;
        if (sort) {
            const parts = sort.split(',');
            if (parts.length === 2) {
                const [field, direction] = parts;
                let backendField = field;
                if (field === 'price') backendField = 'salesPriceWholesale';
                else if (field === 'retailPrice') backendField = 'salesPriceRetail';
                backendSort = `${backendField},${direction}`;
            }
        }

        const response = await apiClient.get('/products', { 
            params: { 
                status, 
                size,
                page,
                search: search || undefined,
                category: category || undefined,
                sort: backendSort
            } 
        });

        // Return the full paginated response with transformed content
        return {
            content: (response.data.content || []).map(transformBackendToFrontend),
            totalElements: response.data.totalElements,
            totalPages: response.data.totalPages,
            pageNumber: response.data.pageNumber
        };
    } catch (error) {
        throw error;
    }
};

/**
 * Get all unique product categories for the current tenant
 */
export const getProductCategories = async () => {
    try {
        const response = await apiClient.get('/products/categories');
        return response.data || [];
    } catch (error) {
        console.error('Error fetching categories:', error);
        return [];
    }
};

/**
 * Get all unique product units for the current tenant
 */
export const getProductUnits = async () => {
    try {
        const response = await apiClient.get('/products/units');
        return response.data || [];
    } catch (error) {
        console.error('Error fetching units:', error);
        return [];
    }
};

/**
 * Create a new product
 */
export const createProduct = async (productData) => {
    try {
        const requestData = transformFrontendToCreateRequest(productData);

        const response = await apiClient.post('/products', requestData);

        // Response is the publicId (String)
        return response.data;
    } catch (error) {
        console.error('Error creating product:', error);
        throw error;
    }
};

/**
 * Update an existing product
 */
export const updateProduct = async (publicId, productData) => {
    try {
        const requestData = transformFrontendToUpdateRequest(productData);

        await apiClient.put(`/products/${publicId}`, requestData);

        // 204 No Content response
        return true;
    } catch (error) {
        console.error('Error updating product:', error);
        throw error;
    }
};

/**
 * Import products from Excel file
 */
export const importProducts = async (file) => {
    try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await apiClient.post('/products/import', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        return response.data; // ProductImportResponse
    } catch (error) {
        console.error('Error importing products:', error);
        throw error;
    }
};

/**
 * Toggle product active status (soft-delete)
 */
export const toggleProductStatus = async (publicId, active) => {
    try {
        await apiClient.patch(`/products/${publicId}/status`, null, { params: { active } });
        return true;
    } catch (error) {
        console.error('Error toggling product status:', error);
        throw error;
    }
};

/**
 * Handle API errors and return user-friendly messages
 */
export const handleApiError = (error) => {
    if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
            case 400:
                return data.message || 'Invalid data. Please check your input.';
            case 401:
                return 'Session expired. Please login again.';
            case 403:
                return 'You do not have permission to perform this action.';
            case 404:
                return 'Product not found.';
            case 500:
                return 'Server error. Please try again later.';
            default:
                return data.message || 'An error occurred. Please try again.';
        }
    } else if (error.request) {
        // Request made but no response
        return 'Network error. Please check your connection.';
    } else {
        // Error in request setup
        return error.message || 'An unexpected error occurred.';
    }
};

/**
 * Request a presigned URL from the backend
 */
export const getPresignedUploadUrl = async (filename, contentType) => {
    try {
        const response = await apiClient.post('/products/images/presigned', {
            filename,
            contentType
        });
        return response.data; // { uploadUrl, publicUrl }
    } catch (error) {
        console.error('Error getting presigned URL:', error);
        throw error;
    }
};

/**
 * Upload binary file data directly to the S3 bucket using a presigned URL
 */
export const uploadImageToPresignedUrl = async (uploadUrl, fileBlob) => {
    const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': 'image/webp'
        },
        body: fileBlob
    });
    if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
    }
    return true;
};
